import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Tag, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const initials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-2.5">
        <Icon size={14} className="text-faint-c mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-faint-c font-mono">{label}</div>
            <div className="mt-0.5 text-sm text-text-primary">
                {value || <span className="text-faint-c italic">не вказано</span>}
            </div>
        </div>
    </div>
);

const ProfileSection = ({ user }) => {
    const navigate = useNavigate();
    const [business, setBusiness] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/business");
                setBusiness(r.data || null);
            } catch {
                setBusiness(null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const hasBusiness = business && (business.name || business.niche);

    return (
        <div className="space-y-5" data-testid="profile-section-content">
            <div className="flex items-center gap-4">
                {user?.picture ? (
                    <img
                        src={user.picture}
                        alt={user.name}
                        className="w-16 h-16 rounded-full border-2 border-border object-cover shrink-0"
                        data-testid="profile-avatar"
                    />
                ) : (
                    <div
                        className="w-16 h-16 rounded-full bg-primary/20 border-2 border-primary/40
                                   flex items-center justify-center text-primary-c font-bold text-xl shrink-0"
                        data-testid="profile-avatar-initials"
                    >
                        {initials(user?.name)}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg text-text-primary truncate"
                         data-testid="profile-name">
                        {user?.name || "—"}
                    </div>
                    <div className="text-sm text-secondary-c truncate font-mono"
                         data-testid="profile-email">
                        {user?.email || "—"}
                    </div>
                </div>
            </div>

            <div className="border-t border-border/60 pt-4">
                <div className="text-xs uppercase tracking-wider font-mono text-faint-c mb-2">
                    Бізнес-дані
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 py-4 text-sm text-faint-c">
                        <Loader2 size={14} className="animate-spin" />
                        Завантаження…
                    </div>
                ) : hasBusiness ? (
                    <div data-testid="profile-business">
                        <InfoRow icon={Briefcase} label="Назва" value={business.name} />
                        <InfoRow icon={Tag} label="Ніша" value={business.niche} />
                        <InfoRow icon={MapPin} label="Регіон / Місто" value={business.city} />
                    </div>
                ) : (
                    <div className="text-sm text-faint-c py-2" data-testid="profile-business-empty">
                        Дані бізнесу ще не заповнено.
                    </div>
                )}

                <button
                    type="button"
                    onClick={() => navigate("/onboarding/step-2")}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                               bg-primary/15 hover:bg-primary/25 border border-primary/30
                               text-primary-c text-xs font-semibold transition-colors"
                    data-testid="profile-edit-business"
                >
                    <span>{hasBusiness ? "Редагувати дані бізнесу" : "Заповнити дані бізнесу"}</span>
                    <ArrowRight size={12} />
                </button>
            </div>

            <div className="text-[11px] text-faint-c leading-relaxed border-t border-border/60 pt-3">
                <span className="font-mono">ℹ️</span> Імʼя, email та аватар підтягуються з вашого Google-акаунту
                і змінюються тільки там.
            </div>
        </div>
    );
};

export default ProfileSection;